import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Camera, Link2, Trash2, RotateCcw, Save, Gift, Mail, MessageCircle, Send, Pencil, Share2 } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { VoiceRecorder } from "@/components/VoiceRecorder";
import { AudioPlayer } from "@/components/AudioPlayer";
import { LabelEditModal } from "@/components/LabelEditModal";
import { RELATIONSHIPS } from "@/components/RelationshipPicker";
import { api, apiError, saveRelationship } from "@/lib/api";
import { SignedImg } from "@/lib/media";
import { toast } from "sonner";

const QUESTIONS = (name) => [
  `Tell us about the first time you knew ${name} was coming.`,
  `What do you remember about the first time you met ${name}?`,
  `What did you feel when you first held or saw ${name}?`,
  `What is one small detail from that day you never want to forget?`,
  `What would you like ${name} to know about what they mean to you?`,
];

export default function Chapter() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [m, setM] = useState(null);
  const [saving, setSaving] = useState(false);
  const [reRecord, setReRecord] = useState(false);
  const [qIndex, setQIndex] = useState(0);
  const [editing, setEditing] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);
  const [editRel, setEditRel] = useState(false);
  const photoInput = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/members/${id}`);
      setM(data);
      setTranscript(data.memory?.transcript || "");
    } catch (e) { toast.error(apiError(e)); navigate("/dashboard"); }
  };
  useEffect(() => { load(); }, [id]);

  const saveMemory = async (blob, duration, name) => {
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("file", blob, name || "memory.webm");
      fd.append("duration", duration);
      toast.info("Saving and gently writing down your words…");
      await api.post(`/members/${id}/memory`, fd);
      setReRecord(false);
      await load();
      toast.success("Your memory is saved.");
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/members/${id}/photo`, fd); await load(); toast.success("Photo added."); }
    catch (err) { toast.error(apiError(err)); }
  };

  const saveTranscript = async () => {
    await api.patch(`/members/${id}/transcript`, { kind: "memory", transcript });
    setEditing(false); await load(); toast.success("Transcript updated.");
  };

  const remove = async () => {
    if (!window.confirm(`Remove ${m.name}'s chapter? This cannot be undone.`)) return;
    await api.delete(`/members/${id}`); navigate("/dashboard");
  };
  const inviteLink = () => `${window.location.origin}/reply/${m.invite_token}`;
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(inviteLink()); toast.success("Private invitation link copied."); }
    catch { document.getElementById("invite-link-field")?.select(); toast.message("Press and hold the link below to copy it."); }
  };
  const shareLink = async () => {
    if (!navigator.share) return copyLink();
    try { await navigator.share({ title: "Memories Forever", text: shareMsg(), url: inviteLink() }); } catch { /* share sheet closed */ }
  };
  const shareMsg = () =>
    `I've recorded a family memory for you on Memories Forever. Please leave me your first memory of me here: ${inviteLink()}`;
  const sendEmail = async () => {
    if (!inviteEmail || inviteEmail.indexOf("@") < 0) return toast.error("Please enter a valid email.");
    setSendingEmail(true);
    try {
      const { data } = await api.post(`/members/${id}/invite`, { email: inviteEmail });
      if (data.status === "queued") toast.success(`Our email service is busy — we'll send the invitation to ${inviteEmail} automatically in a few minutes.`);
      else toast.success(`Invitation emailed to ${inviteEmail}.`);
      await load();
    } catch (e) { toast.error(apiError(e)); } finally { setSendingEmail(false); }
  };

  if (!m) return <div className="min-h-screen legacy-bg"><Navbar /></div>;
  const questions = QUESTIONS(m.name);

  return (
    <div className="min-h-screen legacy-bg">
      <Navbar />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <button onClick={() => navigate("/dashboard")} className="flex items-center gap-2 text-[#B9B3C8] mb-6" data-testid="back-btn">
          <ArrowLeft className="h-5 w-5" /> Back to our family
        </button>

        <div className="flex items-center gap-5 mb-8">
          <div className="relative">
            <div className="h-24 w-24 rounded-full bg-[#1A1222] border border-[#33264A] overflow-hidden flex items-center justify-center">
              {m.photo ? <SignedImg path={m.photo} alt="" className="h-full w-full object-cover" />
                       : <span className="font-serif text-3xl gold-text">{m.name[0]}</span>}
            </div>
            <button onClick={() => photoInput.current?.click()} data-testid="upload-photo-btn"
              className="absolute -bottom-1 -right-1 h-9 w-9 rounded-full gold-btn flex items-center justify-center">
              <Camera className="h-4 w-4" />
            </button>
            <input ref={photoInput} type="file" accept="image/*" onChange={uploadPhoto} className="hidden" />
          </div>
          <div>
            <h1 className="font-serif text-4xl text-[#EDE6F7]">{m.name}</h1>
            <p className="gold-text text-lg" data-testid="chapter-relationship">{m.relationship}{m.birth_year ? ` · born ${m.birth_year}` : ""}</p>
            <button data-testid="chapter-edit-relationship-btn" onClick={() => setEditRel(true)}
              className="mt-1 inline-flex items-center gap-1 text-sm text-[#8C84A0] hover:text-[#B57BFF] transition-colors">
              <Pencil className="h-3.5 w-3.5" /> Edit relationship
            </button>
          </div>
        </div>

        {(!m.memory || reRecord) ? (
          <section className="glass-card p-6 sm:p-8 mb-8">
            <p className="eyebrow mb-3">Record a memory</p>
            <div className="rounded-2xl bg-[#1A1222] border border-[#33264A] p-6 mb-8">
              <p className="text-2xl font-serif text-[#EDE6F7] leading-snug">{questions[qIndex]}</p>
              <button data-testid="next-question-btn" onClick={() => setQIndex((qIndex + 1) % questions.length)}
                className="mt-3 text-sm gold-text underline">Show me another question</button>
            </div>
            <VoiceRecorder onSave={saveMemory} saving={saving} saveLabel="Save this memory" />
            <p className="text-center text-sm text-[#5E5670] mt-4">
              Your recording stays private to your family.{" "}
              <a href="/privacy" target="_blank" rel="noreferrer" className="underline hover:text-[#8C84A0]">How we handle recordings</a>.
            </p>
            {reRecord && (
              <button onClick={() => setReRecord(false)} className="mt-3 text-[#8C84A0] underline mx-auto block">
                Keep the current recording
              </button>
            )}
          </section>
        ) : (
          <section className="glass-card p-6 sm:p-8 mb-8">
            <p className="eyebrow mb-3">Your memory of {m.name}</p>
            <AudioPlayer path={m.memory.audio_path} label="Play your voice memory" testId="memory-player" />
            <div className="mt-5 rounded-2xl bg-[#1A1222] border border-[#33264A] p-5">
              <div className="flex items-center justify-between mb-2">
                <p className="eyebrow">Transcript</p>
                {!editing && (
                  <button data-testid="edit-transcript-btn" onClick={() => setEditing(true)} className="text-sm gold-text underline">
                    Correct names, dates or places
                  </button>
                )}
              </div>
              {editing ? (
                <>
                  <textarea data-testid="transcript-input" value={transcript} onChange={(e) => setTranscript(e.target.value)}
                    className="w-full min-h-32 p-3 rounded-xl bg-[#120B19] border border-[#33264A] text-[#EDE6F7] text-lg" />
                  <button data-testid="save-transcript-btn" onClick={saveTranscript}
                    className="gold-btn h-12 px-5 rounded-full mt-3 flex items-center">
                    <Save className="h-4 w-4 mr-2" /> Save transcript
                  </button>
                </>
              ) : (
                <p className="text-lg text-[#EDE6F7] font-cormorant leading-relaxed">{m.memory.transcript || "—"}</p>
              )}
            </div>
            <button data-testid="rerecord-memory-btn" onClick={() => setReRecord(true)}
              className="ghost-gold h-12 px-5 rounded-full mt-5 flex items-center">
              <RotateCcw className="h-4 w-4 mr-2" /> Record this memory again
            </button>
          </section>
        )}

        {m.memory && !reRecord && (
          <section className="glass-card p-6 sm:p-8 mb-8" data-testid="reply-section">
            {m.reply ? (
              <div data-testid="reply-received">
                <p className="eyebrow mb-3">{m.name}'s reply</p>
                <div className="flex items-center gap-3 text-[#B57BFF]">
                  <Gift className="h-6 w-6 shrink-0" />
                  <span className="text-lg" data-testid="reply-received-text">{m.name}'s reply has returned — saved for "Your Memories of Me".</span>
                </div>
              </div>
            ) : (
              <>
              <p className="eyebrow mb-3" data-testid="invite-heading">Invite {m.name} to reply</p>
              <p className="text-lg text-[#B9B3C8] mb-5">
                Send {m.name} their own private link. They'll record their first memory of you — kept as a surprise for your story.
              </p>
              <div className="space-y-5" data-testid="invite-panel">
                <div>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <button data-testid="copy-invite-link-btn" onClick={copyLink} className="gold-btn h-14 px-6 rounded-full text-lg flex items-center justify-center">
                      <Link2 className="h-5 w-5 mr-2" /> Copy invitation link
                    </button>
                    <button data-testid="share-invite-link-btn" onClick={shareLink} className="ghost-gold h-14 px-6 rounded-full text-lg flex items-center justify-center">
                      <Share2 className="h-5 w-5 mr-2" /> Share invitation
                    </button>
                  </div>
                  <input id="invite-link-field" readOnly value={inviteLink()} onFocus={(e) => e.target.select()} data-testid="invite-link-field" aria-label="Private invitation link"
                    className="mt-3 w-full h-12 px-4 rounded-xl bg-[#120B19] border border-[#33264A] text-[#C9C2D8] text-sm" />
                </div>
                <div>
                  <label className="eyebrow block mb-2">Send by email</label>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <input data-testid="invite-email-input" type="email" value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)} placeholder={`${m.name}'s email address`}
                      className="flex-1 h-14 px-4 rounded-xl bg-[#1A1222] border border-[#33264A] text-[#EDE6F7] text-lg placeholder:text-[#8C84A0] focus:outline-none focus:border-[#8B2CFF]" />
                    <button data-testid="send-email-btn" onClick={sendEmail} disabled={sendingEmail}
                      className="gold-btn h-14 px-6 rounded-full text-lg flex items-center justify-center disabled:opacity-60">
                      <Mail className="h-5 w-5 mr-2" /> {sendingEmail ? "Sending…" : "Email the link"}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="eyebrow block mb-2">Or share by text</label>
                  <div className="flex flex-wrap gap-3">
                    <a data-testid="share-whatsapp-btn" href={`https://wa.me/?text=${encodeURIComponent(shareMsg())}`}
                      target="_blank" rel="noreferrer" className="ghost-gold h-13 px-5 py-3 rounded-full text-lg flex items-center">
                      <MessageCircle className="h-5 w-5 mr-2" /> WhatsApp
                    </a>
                    <a data-testid="share-sms-btn" href={`sms:?&body=${encodeURIComponent(shareMsg())}`}
                      className="ghost-gold h-13 px-5 py-3 rounded-full text-lg flex items-center">
                      <Send className="h-5 w-5 mr-2" /> Text message
                    </a>
                  </div>
                </div>
              </div>
              </>
            )}
          </section>
        )}

        <button onClick={remove} data-testid="delete-member-btn" className="flex items-center gap-2 text-[#7A2E4F] hover:text-[#EF4444] mx-auto">
          <Trash2 className="h-4 w-4" /> Remove {m.name}'s chapter
        </button>
      </main>
      {editRel && (
        <LabelEditModal testid="edit-relationship-modal" kind="rel" options={RELATIONSHIPS} initial={m.relationship}
          title="Edit relationship" intro={`How is ${m.name} related to you? Their recordings stay exactly as they are.`}
          inputLabel="What is their relationship to you?" placeholder="e.g. Niece, Godson, Stepdaughter"
          onSave={async (v) => { await saveRelationship(m.id, v); await load(); }} onClose={() => setEditRel(false)} />
      )}
    </div>
  );
}
